import { supabase } from "@/integrations/supabase/client";
import type { MessagingIntegrationOption } from "@/lib/member-accessible-integrations";

export type StartLeadConversationResult = {
  ok: boolean;
  conversation?: {
    id: string;
    channel: string;
    state?: string;
    followup_enabled?: boolean;
    is_closed?: boolean;
    integration_id?: string | null;
  };
  isNew?: boolean;
  needsIntegrationChoice?: boolean;
  integrations?: MessagingIntegrationOption[];
  error?: string;
  conflict?: PhoneConflict;
};

export type PhoneConflict = {
  can_see: boolean;
  lead_id: string | null;
  name: string | null;
  owner_display: string | null;
  funnel_name: string | null;
};

/** Texto único para "este número já pertence a outro contato". Nunca expõe dados de quem não pode ver. */
export function describePhoneConflict(c: Partial<PhoneConflict> | null | undefined): string {
  if (!c) return "Já existe um contato com esse telefone neste workspace.";
  if (c.can_see && c.name) return `Este número já pertence ao contato "${c.name}".`;
  const owner = c.owner_display ? ` de ${c.owner_display}` : " sem responsável";
  const funnel = c.funnel_name ? ` no funil "${c.funnel_name}"` : "";
  return `Este número já pertence a um contato${owner}${funnel}. Peça acesso a um administrador.`;
}

export async function startLeadConversation(params: {
  workspaceId: string;
  leadId: string;
  channel: "whatsapp" | "instagram" | "messenger";
  integrationId?: string | null;
  phone?: string | null;
}): Promise<StartLeadConversationResult> {
  const { data, error } = await supabase.functions.invoke("start-conversation", {
    body: {
      workspaceId: params.workspaceId,
      leadId: params.leadId,
      channel: params.channel,
      integrationId: params.integrationId ?? undefined,
      phone: params.phone ?? undefined,
    },
  });

  if (error) {
    const message =
      typeof error === "object" && error !== null && "message" in error
        ? String((error as { message?: string }).message)
        : "Não foi possível criar a conversa.";
    return { ok: false, error: message };
  }

  if (data?.needsIntegrationChoice) {
    return {
      ok: false,
      needsIntegrationChoice: true,
      integrations: (data.integrations ?? []).map(
        (integration: { id: string; display_name: string }) => ({
          id: integration.id,
          display_name: integration.display_name,
          provider: "whatsapp",
        }),
      ),
    };
  }

  if (data?.conflict) {
    const ex = data.existing ?? {};
    return {
      ok: false,
      conflict: {
        can_see: ex.can_see === true,
        lead_id: ex.lead_id ?? null,
        name: ex.name ?? null,
        owner_display: ex.owner_display ?? null,
        funnel_name: ex.funnel_name ?? null,
      },
      error: describePhoneConflict(ex),
    };
  }

  if (data?.error) {
    return { ok: false, error: String(data.error) };
  }

  if (!data?.conversation) {
    return { ok: false, error: "Não foi possível criar a conversa." };
  }

  return {
    ok: true,
    conversation: data.conversation,
    isNew: data.isNew === true,
  };
}
